import React, {useState, useEffect, useRef} from 'react';
import {Box} from '@mui/material';
import {Body, Icon, designSystemToken} from '@lightricks/react-design-system';

interface DropdownOption {
  id: string;
  name: string;
  overridingFontFamily?: string;
}

interface StyleEditorDropdownProps {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
}

interface DropdownButtonProps {
  displayValue: string;
  isOpen: boolean;
  onClick: () => void;
  selectedOption?: DropdownOption;
}

interface DropdownMenuProps {
  options: DropdownOption[];
  selectedValue: string;
  isClosing: boolean;
  onOptionClick: (value: string) => void;
}

interface DropdownOptionItemProps {
  option: DropdownOption;
  isSelected: boolean;
  onClick: () => void;
}

// Helper Components
function DropdownButton({
  displayValue,
  isOpen,
  onClick,
  selectedOption,
}: DropdownButtonProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '8px 12px',
        borderRadius: '8px',
        border: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
        backgroundColor: designSystemToken('semantic.bg.primary'),
        cursor: 'pointer',
        height: '40px',
        boxSizing: 'border-box',
      }}
    >
      <Body
        size="md"
        color={designSystemToken('semantic.fg.primary')}
        style={{
          fontFamily: selectedOption?.overridingFontFamily || 'inherit',
        }}
      >
        {displayValue}
      </Body>
      <Icon
        name="Actions-Arrow-Down-Mini"
        size="large"
        appearance="neutral"
        color={designSystemToken('semantic.fg.alpha-secondary')}
        style={{
          transform: isOpen ? 'rotate(180deg)' : 'unset',
          transition: 'transform 0.2s ease',
          width: '24px',
          height: '24px',
        }}
      />
    </Box>
  );
}

function DropdownOptionItem({
  option,
  isSelected,
  onClick,
}: DropdownOptionItemProps) {
  return (
    <Box
      key={option.id}
      onClick={onClick}
      sx={{
        'padding': '12px 16px',
        'cursor': 'pointer',
        'backgroundColor': isSelected
          ? designSystemToken('semantic.bg.tertiary')
          : 'transparent',
        '&:hover': {
          backgroundColor: designSystemToken('semantic.bg.tertiary'),
        },
      }}
    >
      <Body
        size="md"
        color={designSystemToken('semantic.fg.primary')}
        style={{
          fontFamily: option.overridingFontFamily || 'inherit',
        }}
      >
        {option.name}
      </Body>
    </Box>
  );
}

function DropdownMenu({
  options,
  selectedValue,
  isClosing,
  onOptionClick,
}: DropdownMenuProps) {
  return (
    <Box
      sx={{
        'position': 'absolute',
        'top': '100%',
        'left': 0,
        'right': 0,
        'mt': '4px',
        'backgroundColor': designSystemToken('semantic.bg.primary'),
        'border': `1px solid ${designSystemToken(
          'semantic.bg.neutral-secondary'
        )}`,
        'borderRadius': '8px',
        'boxShadow': '0 4px 12px rgba(0, 0, 0, 0.1)',
        'zIndex': 1000,
        'maxHeight': '200px',
        'overflowY': 'auto',
        'transformOrigin': 'top',
        'animation': isClosing
          ? 'dropdownSlideOut 0.15s ease-in forwards'
          : 'dropdownSlideIn 0.2s ease-out',
        '@keyframes dropdownSlideIn': {
          '0%': {
            opacity: 0,
            transform: 'scaleY(0.8) translateY(-8px)',
          },
          '100%': {
            opacity: 1,
            transform: 'scaleY(1) translateY(0)',
          },
        },
        '@keyframes dropdownSlideOut': {
          '0%': {
            opacity: 1,
            transform: 'scaleY(1) translateY(0)',
          },
          '100%': {
            opacity: 0,
            transform: 'scaleY(0.9) translateY(-4px)',
          },
        },
      }}
    >
      {options.map((option) => (
        <DropdownOptionItem
          key={option.id}
          option={option}
          isSelected={option.id === selectedValue}
          onClick={() => onOptionClick(option.id)}
        />
      ))}
    </Box>
  );
}

function StyleEditorDropdown({
  value,
  options,
  onChange,
  placeholder = 'Select...',
}: StyleEditorDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.id === value);
  const displayValue = selectedOption?.name || placeholder;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 150);
  };

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    handleClose();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isClickInside = dropdownRef.current?.contains(event.target as Node);
      if (isOpen && dropdownRef.current && !isClickInside) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <Box position="relative" width="100%" ref={dropdownRef}>
      <DropdownButton
        displayValue={displayValue}
        isOpen={isOpen}
        onClick={() => (isOpen ? handleClose() : setIsOpen(true))}
        selectedOption={selectedOption}
      />
      {isOpen && (
        <DropdownMenu
          options={options}
          selectedValue={value}
          isClosing={isClosing}
          onOptionClick={handleOptionClick}
        />
      )}
    </Box>
  );
}

export default StyleEditorDropdown;
